"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Chapter = {
  number: string;
  title: string;
  heading: string;
  description: string;
  caption: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const chapters: Chapter[] = [{
  number: "01",
  title: "The Foundation",
  heading: "Where structural integrity meets vision.",
  description: "Every project begins with a deep understanding of your goals. We listen to your needs, assess the site, and lay the groundwork for a construction process built on transparency and precision.",
  caption: "Initial consultation — site assessment",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/foundation_planning.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/foundation_planning.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/foundation_planning.webp-wvc-sizes",
  alt: "Americo Moscariello reviewing blueprints on site"
}, {
  number: "02",
  title: "The Sourcing",
  heading: "Quality materials, expertly procured.",
  description: "We partner with trusted suppliers to ensure every beam, brick, and finish meets our rigorous standards. We believe that the longevity of a structure depends on the quality of its components.",
  caption: "Material selection — vendor coordination",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/material_sourcing.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/material_sourcing.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/material_sourcing.png-wvc-sizes",
  alt: "Construction materials staged for project"
}, {
  number: "03",
  title: "The Construction",
  heading: "Building with craftsmanship and care.",
  description: "Our team executes the build with meticulous attention to detail. From framing to final trim, we maintain a clean, safe, and efficient job site, ensuring your project progresses on schedule.",
  caption: "Active build — site management",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/active_construction.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/active_construction.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/active_construction.png-wvc-sizes",
  alt: "Construction crew working on residential framing"
}, {
  number: "04",
  title: "The Inspection",
  heading: "Standards that exceed expectations.",
  description: "Before we consider a phase complete, we conduct thorough internal inspections. We verify that every element aligns with local codes and our own high benchmarks for durability and aesthetics.",
  caption: "Quality control — safety verification",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/site_inspection.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/site_inspection.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/site_inspection.webp-wvc-sizes",
  alt: "Professional inspection of structural work"
}, {
  number: "05",
  title: "The Stewardship",
  heading: "Protecting your investment long-term.",
  description: "Construction is only the beginning. Our property management services ensure your building remains in peak condition, providing proactive maintenance and responsive support for years to come.",
  caption: "Property management — ongoing care",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_stewardship.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_stewardship.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_stewardship.png-wvc-sizes",
  alt: "Well-maintained property exterior"
}, {
  number: "06",
  title: "The Completion",
  heading: "A legacy built to last.",
  description: "We hand over the keys with pride, knowing we have delivered a space that reflects your vision. At Moscariello Construction, we don't just build structures; we build lasting relationships.",
  caption: "Project handover — client satisfaction",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/project_completion.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/project_completion.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/project_completion.webp-wvc-sizes",
  alt: "Finished construction project ready for occupancy"
}];
function ChapterRow({
  chapter,
  i,
  onInView
}: {
  chapter: Chapter;
  i: number;
  onInView: (index: number) => void;
}) {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25,
    rootMargin: "0px 0px -10% 0px"
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25,
    rootMargin: "0px 0px -10% 0px"
  });
  const rowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = rowRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) onInView(i);
      });
    }, {
      threshold: 0.5
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [i, onInView]);
  const isOdd = i % 2 === 0;
  return <div data-index={i} ref={rowRef} className="relative grid grid-cols-12 gap-4 md:gap-6 mb-24 md:mb-40 last:mb-0">
      {}
      <div className="hidden md:block absolute left-1/4 -translate-x-1/2 top-2 z-10" aria-hidden="true">
        <div className="w-4 h-4 rounded-lg bg-primary shadow-lg" />
      </div>

      {}
      <div ref={textRef} className={`col-span-12 md:col-span-5 ${isOdd ? "md:col-start-6" : "md:col-start-6"} pl-12 md:pl-8 transition-all duration-1000 ease-out ${textVisible ? "opacity-100 translate-x-0" : isOdd ? "opacity-0 translate-x-10" : "opacity-0 -translate-x-10"}`} style={{
      transitionDelay: "0ms"
    }}>
        <div className="font-serif text-5xl md:text-6xl text-primary leading-none mb-6">
          {chapter.number}
        </div>
        <div className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-5">
          {chapter.title}
        </div>
        <h3 className="font-default text-2xl md:text-4xl leading-tight text-foreground mb-6">
          {chapter.heading}
        </h3>
        <p className="font-default text-base md:text-[17px] leading-[1.7] text-muted-foreground mb-6 max-w-xl">
          {chapter.description}
        </p>
        <p className="font-mono text-xs tracking-wider uppercase text-secondary">
          {chapter.caption}
        </p>
      </div>

      {}
      <div ref={imageRef} className={`col-span-12 ${isOdd ? "md:col-span-3 md:col-start-11 md:mt-10" : "md:col-span-3 md:col-start-2 md:mt-10"} pl-12 md:pl-0 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 [clip-path:inset(0_0_0_0)]" : "opacity-0 [clip-path:inset(0_0_100%_0)]"}`} style={{
      transitionDelay: "200ms"
    }}>
        <div className="overflow-hidden rounded-lg shadow-lg">
          <img src={chapter.image} data-wvc-srcset={chapter.srcset} data-wvc-sizes={chapter.sizes} alt={chapter.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-[4/5]" />
        </div>
      </div>
    </div>;
}
export default function LegacyTimeline() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: lineRef,
    isVisible: lineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05,
    rootMargin: "0px 0px -20% 0px"
  });
  const [activeIndex, setActiveIndex] = useState(0);
  return <section data-section-id="3042" id="legacytimeline" className="bg-background text-foreground py-24 md:py-44 px-6 md:px-12 overflow-hidden dark">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-12 gap-4 md:gap-6 mb-20 md:mb-32 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="col-span-12 md:col-span-7 md:col-start-2">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-primary mb-8">
              The Moscariello Process &mdash; Building Excellence
            </div>
            <h2 className="font-default text-4xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground mb-8">
              Six phases of <em className="font-light italic">uncompromising</em> quality.
            </h2>
            <p className="font-default italic text-lg md:text-xl leading-relaxed text-muted-foreground max-w-2xl">
              At Moscariello Construction, every project is a commitment to integrity. From the first blueprint to the final inspection, we build with the precision your property deserves.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div ref={lineRef} className="hidden md:block absolute left-1/4 -translate-x-1/2 top-0 bottom-0 w-px" aria-hidden="true">
            <div className="w-full bg-primary origin-top transition-transform duration-[2000ms] ease-out" style={{
            height: "100%",
            transform: lineVisible ? "scaleY(1)" : "scaleY(0)"
          }} />
          </div>

          {}
          <div className="md:hidden absolute left-4 top-0 bottom-0 w-px bg-primary/60" aria-hidden="true" />

          {}
          <div className="md:hidden absolute left-4 -translate-x-1/2 flex flex-col gap-0 pointer-events-none">
            {chapters.map((_, i) => <div key={i} data-index={i} className={`w-3 h-3 rounded-lg bg-primary mb-24 transition-all duration-500 ${activeIndex === i ? "scale-150 shadow-lg" : ""}`} />)}
          </div>

          {chapters.map((chapter, i) => <ChapterRow key={chapter.number} chapter={chapter} i={i} onInView={setActiveIndex} />)}
        </div>
      </div>
    </section>;
}